import FlexSearch from 'flexsearch';
import EnglishBookPreset from "flexsearch/lang/en";

export const SEARCH_SORT_SCORE_ALPHA = 'SEARCH_SORT_SCORE_ALPHA';

export const SEARCH_SORT_ALPHA_ASC = 'SEARCH_SORT_ALPHA_ASC';

export const SEARCH_SORT_ALPHA_DESC = 'SEARCH_SORT_ALPHA_DESC';

export const SEARCH_SORT_DATE_ASC = 'SEARCH_SORT_DATE_ASC';

export const SEARCH_SORT_DATE_DESC = 'SEARCH_SORT_DATE_DESC';

// Defines per field index config
const SEARCH_FIELDS = [
  {
    field: 'exemptionTitle',
  },
  {
    field: 'enactingStatement',
  },
  {
    field: 'exemptionContent',
  },
  {
    field: 'conditions',
  },
  {
    field: 'statementOfReasons',
  },
  {
    field: 'contact',
  },
  {
    field: 'corrigendum',
  },
  {
    field: 'legislationType',
  },
  {
    field: 'makerName',
  },
  {
    field: 'relatedInstruments',
  }
];

// Defines fields to be stored as tags
const TAG_FIELDS = [
  {
    field: 'sectors'
  },
  // {
  //   field: 'expired'
  // },
];

// Fields to be returned with result object, to be displayed or used in front-end logic
const STORE_FIELDS = [
  {
    field: 'madeDate',
  },
  {
    field: 'expired'
  },
  {
    field: 'pageLink'
  },
  {
    field: 'id'
  },
  {
    field: 'exemptionTitle',
  },
  ...TAG_FIELDS,
];

// Define the per field boost
const SEARCH_FIELD_BOOST = {
  exemptionTitle: 1,
  exemptionContent: 0.5,
  conditions: 0.5,
  statementOfReasons: 0.5,
  contact: 0.1,
  corrigendum: 0.5,
  legislationType: 0.1,
  makerName: 0.5,
  relatedInstruments: 0.5
};

export function createIndex(data: object) {

  // This encoder preset manages char normalization and contains a sensible set of stopwords
  const encoder = new FlexSearch.Encoder(EnglishBookPreset);

  const index = new FlexSearch.Document({
    tokenize: 'forward',
    context: true,
    id: 'id',
    index: SEARCH_FIELDS,
    tag: TAG_FIELDS,
    store: STORE_FIELDS,
    encoder: encoder,
  });

  data.forEach((value) => index.add(value));

  return index;
}

export function search(
  index: any,
  term: string,
  sort: string|null = null,
  tags: []|null = null,
  allWhenEmptyTerm: boolean = true, // When the search term is empty, return all data in the index
) {
  const rankedResults = {};
  let results = [];

  if (term || tags.sectors.length) {
    // Transform raw results into map from key -> { score, data }.
    index.search({
      query: term,
      tag: {
        "sectors": [tags.sectors].filter(e => e),
      },
    }).forEach((value) => {
      value.result.forEach((key) => {
        if (!rankedResults.hasOwnProperty(key)) {
          rankedResults[key] = {
            score: 0,
            data: index.get(key),
          };
        }

        rankedResults[key].score += SEARCH_FIELD_BOOST[value.field];
      });
    });

    results = Object.values(rankedResults);

  } else if (allWhenEmptyTerm) {
    index.store.forEach((value) => {
      results.push({
        score: 1,
        data: value,
      });
    });
  }

  // Filter by expiry status, multi-tag searches using AND operator require the use of resolvers.
  if (tags.expired) {
    results = results.filter(result => result.data.expired === tags.expired);
  }

  // Sort by score and then by title ascending
  let sortFunc = (a, b) =>
    b.score - a.score ||
    a.data.exemptionTitle.localeCompare(b.data.exemptionTitle);

  switch (sort) {
    // Sort by title ascending
    case SEARCH_SORT_ALPHA_ASC:
      sortFunc = (a, b) =>
        a.data.exemptionTitle.localeCompare(b.data.exemptionTitle);

      break;
    // Sort by title descending
    case SEARCH_SORT_ALPHA_DESC:
      sortFunc = (a, b) =>
        b.data.exemptionTitle.localeCompare(a.data.exemptionTitle);

      break;

    // Sort by date descending
    case SEARCH_SORT_DATE_DESC:
      sortFunc = (a, b) =>
        new Date(b.data.madeDate).getTime() - new Date(a.data.madeDate).getTime();

      break;

    // Sort by date ascending
    case SEARCH_SORT_DATE_ASC:
      sortFunc = (a, b) =>
        new Date(a.data.madeDate).getTime() - new Date(b.data.madeDate).getTime();

      break;
  }

  return results.sort(sortFunc);
}
